/**
 * Hand-curated shared domain types. The OpenAPI-generated client lives in
 * `@eon/api-client`; this package keeps small, ergonomic projections used
 * across the apps (selectors, view models, enums) and re-exports nothing
 * by default to avoid leaking the entire OpenAPI surface.
 */

export type UUID = string;
export type ISODateTime = string;

/**
 * Canonical user roles understood by the backend (see
 * `internal/etl/transformers.go::normaliseRole` and
 * `internal/api/http/router.go` `RequireRole(...)`).
 *
 * Hierarchy (highest → lowest privilege):
 *   system_admin  – platform super admin (full access)
 *   reseller      – distributor/partner with cross-org visibility
 *   technical     – field engineer (device register/operate)
 *   admin         – organization administrator
 *   owner         – organization owner
 *   manager       – organization manager
 *   member        – regular org member / customer
 *   viewer        – read-only
 */
export type Role =
  | 'system_admin'
  | 'reseller'
  | 'technical'
  | 'admin'
  | 'owner'
  | 'manager'
  | 'member'
  | 'viewer';

export type AppType = 'system' | 'organization' | 'customer';

/** Top-of-hierarchy roles allowed into the system admin web app. */
export const SYSTEM_ADMIN_ROLES: readonly Role[] = ['system_admin'] as const;

/**
 * Org operator roles for the unified web app's portal surface and
 * `/api/om/*` (matches backend `RequireRole("owner", "admin", "manager", "system_admin")`
 * minus platform admins, who use the admin surface instead).
 */
export const ORG_OPERATOR_ROLES: readonly Role[] = ['owner', 'admin', 'manager'] as const;

/** @deprecated Use {@link ORG_OPERATOR_ROLES}. Kept for existing imports. */
export const ORG_PORTAL_ROLES = ORG_OPERATOR_ROLES;

/** Roles allowed to sign in to the unified web application. */
export const WEB_APP_ROLES: readonly Role[] = [
  'system_admin',
  ...ORG_OPERATOR_ROLES,
] as const;

export function hasRole(role: Role | undefined | null, allowed: readonly Role[]): boolean {
  return !!role && allowed.includes(role);
}

export function isSystemAdmin(role: Role | undefined | null): boolean {
  return hasRole(role, SYSTEM_ADMIN_ROLES);
}

export function isOrgOperator(role: Role | undefined | null): boolean {
  return hasRole(role, ORG_OPERATOR_ROLES);
}

export type DeviceStatus =
  | 'available'
  | 'preparing'
  | 'charging'
  | 'suspended_ev'
  | 'suspended_evse'
  | 'finishing'
  | 'reserved'
  | 'unavailable'
  | 'faulted'
  | 'offline'
  | 'unknown';

export type SessionStatus = 'started' | 'charging' | 'stopping' | 'stopped' | 'aborted';

/**
 * Canonical paginated envelope. Mirrors `internal/pkg/pagination.Result[T]`
 * on the backend; `data` is the page slice, totals describe the full set.
 */
export interface Page<T> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface User {
  id: UUID;
  email?: string;
  phone?: string | null;
  name: string;
  surname?: string;
  role: Role;
  appType: AppType;
  active: boolean;
  registrationComplete?: boolean;
  /** Optional — present in admin list endpoints, not /my/profile. */
  createdAt?: ISODateTime;
  organizations?: { id: UUID; name: string }[];
  vehicles?: unknown[];
  wallets?: unknown[];
}

export interface AuthSession {
  accessToken: string;
  appType: AppType;
  /** Populated client-side after login by fetching /my/profile. */
  user?: User;
}

export interface StationDeviceSummary {
  id: UUID;
  serialNumber: string;
  displayName?: string | null;
  status?: string | null;
  registered?: boolean;
}

export interface Station {
  id: UUID;
  name: string;
  description?: string | null;
  organizationId?: UUID | null;
  address?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  city?: string | null;
  country?: string | null;
  isPublic?: boolean;
  isActive?: boolean;
  active: boolean;
  deviceCount: number;
  contact?: Record<string, unknown> | null;
  operatingHours?: Record<string, unknown> | null;
  amenities?: string[];
  metadata?: Record<string, unknown> | null;
  devices?: StationDeviceSummary[];
  createdAt?: ISODateTime | null;
  updatedAt?: ISODateTime | null;
}

export interface Connector {
  id: number;
  status: string;
  type?: string | null;
  format?: string | null;
  errorCode?: string | null;
  info?: string | null;
  powerKW?: number | null;
  currentAmps?: number | null;
  voltage?: number | null;
  /** Optional convenience alias maintained for backwards compat. */
  maxKW?: number | null;
  updatedAt?: ISODateTime | null;
}

export interface Device {
  id: UUID;
  serialNumber: string;
  displayName?: string | null;
  description?: string | null;
  vendor?: string | null;
  model?: string | null;
  firmware?: string | null;
  firmwareVersion?: string | null;
  ocppVersion?: string | null;
  status: DeviceStatus;
  lastHeartbeat?: ISODateTime | null;
  lastHeartbeatAt?: ISODateTime | null;
  onlineSince?: ISODateTime | null;
  organizationId?: UUID | null;
  stationId?: UUID | null;
  registered?: boolean;
  isPublic?: boolean;
  isActive?: boolean;
  qrToken?: string | null;
  metadata?: Record<string, unknown> | null;
  connectors: Connector[];
  createdAt?: ISODateTime;
  updatedAt?: ISODateTime;
}

export interface ChargingSession {
  id: UUID;
  deviceId: UUID;
  connectorId: number;
  transactionId?: number | null;
  userId?: UUID | null;
  idTag?: string | null;
  status: SessionStatus;
  startedAt: ISODateTime;
  stoppedAt?: ISODateTime | null;
  durationSeconds?: number | null;
  totalEnergyKWh?: number | null;
  meanPowerKW?: number | null;
  meterStart?: number | null;
  meterStop?: number | null;
  /** Enriched display fields (admin / OM list responses). */
  userName?: string | null;
  deviceDisplayName?: string | null;
  stationName?: string | null;
  organizationName?: string | null;
  groupName?: string | null;
}
